import { useEffect, useState } from "react";
import { Icon } from "./Icon";
import { getAppConfig, type AppEnv } from "../sim/env";

/** What /api/build says this server is running (server/buildInfo.js). */
interface BuildInfo { short: string | null; branch: string | null; builtAt: string | null; trains: Array<{ id: string; title: string; changes: number; held: boolean }> }

/**
 * A fixed, unmistakable "TEST SERVER" ribbon shown site-wide whenever the client is talking to the
 * STAGING deployment (env === "staging"). Renders nothing on production. This is the at-a-glance
 * signal that you're on the throwaway test environment — so a change tried here is never mistaken for
 * live, and vice-versa.
 *
 * It also names the BUILD — its commit, and (hover) the release trains it carries (docs/RELEASES.md) — so with
 * months of held work on test, "is my change in this build?" is answered on the ribbon rather than in a terminal.
 */
export function EnvBanner() {
  const [env, setEnv] = useState<AppEnv | null>(null);
  const [build, setBuild] = useState<BuildInfo | null>(null);
  useEffect(() => { let alive = true; getAppConfig().then((c) => { if (alive) setEnv(c.env); }); return () => { alive = false; }; }, []);
  useEffect(() => {
    if (env !== "staging") return;
    let alive = true;
    fetch("/api/build", { cache: "no-store" }).then((r) => (r.ok ? r.json() : null)).then((b) => { if (alive && b) setBuild(b); }).catch(() => {});
    return () => { alive = false; };
  }, [env]);
  if (env !== "staging") return null;
  const trains = build?.trains ?? [];
  const title = ["Changes here do not affect live players",
    build?.short ? `Build ${build.short}${build.builtAt ? ` · ${build.builtAt}` : ""}` : "",
    ...trains.map((t) => `${t.held ? "🔒 " : ""}${t.title}: ${t.changes} change${t.changes === 1 ? "" : "s"}`)].filter(Boolean).join("\n");
  return (
    <div className="env-banner" role="status" aria-label="Test server" title={title} data-testid="env-banner">
      <Icon name="bottle" size={13} /> TEST SERVER
      {build?.short && <span className="env-banner-build"> · {build.short}{trains.some((t) => t.held) ? ` · ${trains.filter((t) => t.held).map((t) => t.title).join(", ")}` : ""}</span>}
    </div>
  );
}
